
import React from 'react';
import { format } from 'date-fns';
import { 
  Video,
  Phone,
  Building,
  CalendarPlus
} from 'lucide-react';
import { WeeklyScheduleType, TimeBlock } from './WeeklySchedule';
import { Button } from '@/components/ui/button';

interface AvailabilityCalendarProps {
  schedule: WeeklyScheduleType;
  onDaySelect?: (day: string) => void;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({ schedule, onDaySelect }) => {
  const days = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ] as const;
  
  // Generate time slots from 6 AM to 10 PM in 1-hour intervals
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6; // Start at 6 AM
    return {
      hour,
      label: `${hour > 12 ? hour - 12 : hour} ${hour >= 12 ? 'PM' : 'AM'}`
    };
  });
  
  // Check if a time block covers a specific hour for a day
  const getBlockForHour = (dayKey: keyof WeeklyScheduleType, hour: number) => {
    const daySchedule = schedule[dayKey];
    if (!daySchedule.isAvailable) return null;
    
    // Convert hour to 24-hour format string (e.g., "09:00")
    const hourStr = `${hour.toString().padStart(2, '0')}:00`;
    
    // Find blocks that cover this hour
    return daySchedule.timeBlocks.find(block => {
      const startHour = parseInt(block.startTime.split(':')[0], 10);
      const endHour = parseInt(block.endTime.split(':')[0], 10);
      return startHour <= hour && (endHour > hour || (endHour === hour && block.endTime.split(':')[1] !== '00'));
    });
  };
  
  const getConsultationTypeColor = (type: string) => {
    switch (type) {
      case 'clinic':
        return 'bg-blue-100 border-blue-300';
      case 'video':
        return 'bg-purple-100 border-purple-300';
      case 'voice':
        return 'bg-green-100 border-green-300';
      default:
        return 'bg-gray-100 border-gray-300';
    }
  };
  
  const getConsultationTypeIcon = (type: string) => {
    switch (type) {
      case 'clinic':
        return <Building className="h-3 w-3 text-blue-600" />;
      case 'video':
        return <Video className="h-3 w-3 text-purple-600" />;
      case 'voice':
        return <Phone className="h-3 w-3 text-green-600" />;
      default:
        return null;
    }
  };

  const handleDayClick = (day: string) => {
    if (onDaySelect) {
      onDaySelect(day);
    }
  };
  
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[768px]">
        {/* Header row with days */}
        <div className="grid grid-cols-8 border-b">
          <div className="p-2 text-center font-medium text-muted-foreground">Time</div>
          {days.map((day) => (
            <div 
              key={day.key} 
              className={`p-2 text-center font-medium cursor-pointer hover:bg-accent/50 ${!schedule[day.key].isAvailable ? 'text-muted-foreground' : ''}`}
              onClick={() => handleDayClick(day.key)}
            >
              {day.label}
            </div>
          ))}
        </div>
        
        {/* Time slots grid */}
        {timeSlots.map((slot) => (
          <div key={slot.hour} className="grid grid-cols-8 border-b">
            <div className="p-2 text-center text-sm text-muted-foreground border-r">
              {slot.label}
            </div>
            
            {days.map((day) => {
              const block = getBlockForHour(day.key, slot.hour);
              return (
                <div 
                  key={day.key} 
                  className={`p-1 border-r min-h-[60px] cursor-pointer hover:bg-accent/10 ${!schedule[day.key].isAvailable ? 'bg-muted/20' : ''}`}
                  onClick={() => handleDayClick(day.key)}
                >
                  {block ? (
                    <div className={`h-full rounded p-1 flex flex-wrap gap-1 text-xs border`}>
                      {block.consultationTypes.map(type => (
                        <div 
                          key={type} 
                          className={`flex items-center rounded px-1 border ${getConsultationTypeColor(type)}`}
                        >
                          {getConsultationTypeIcon(type)}
                          <span className="ml-1 hidden md:inline">{type}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    schedule[day.key].isAvailable && (
                      <div className="h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <CalendarPlus className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      
      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-4">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300 mr-1"></div>
          <span className="text-xs text-muted-foreground">In-clinic</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300 mr-1"></div>
          <span className="text-xs text-muted-foreground">Video</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300 mr-1"></div>
          <span className="text-xs text-muted-foreground">Voice</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
